import {
  difference,
  get,
} from 'lodash';
import {
  eventLocations,
  eventProperties,
  events,
  implementationChecklistTasks,
  keepGoingNotificationId,
  onboardingFeatures,
  onboardingTasks,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { implementationChecklists, tasks } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import KeepGoingNotification from 'client/components/ImplementationChecklist/components/KeepGoingNotification.vue';
import store from 'client/store/index';

const {
  boolean,
  description,
  location,
  section,
  text,
} = eventProperties;

export const createSubscribeActionHandler = (theStore = store) => ({
  /**
   * When adding a resource, it is difficult to tell whether a new item is being created or an
   * existing item is being saved because "itemState.create" is no longer true in the "after" fn.
   * To differentiate, we save the resource's name in the "before" fn and check if the name matches
   * in the "after" fn to tell if an item is being created.
   */
  implementationChecklistComplete: false,
  radiusServerNameToBeCreated: '',
  softwareAppDisplayNameToBeCreated: '',
  usernameOfUserToBeCreated: '',

  before(action, state) {
    switch (action.type) {
      case 'CommandModel/saveItem':
        this.handleBeforeCommandSaved(action, state);
        break;

      case 'ConfigurationModel/saveItem':
        this.handleBeforeConfigurationSaved(action, state);
        break;

      case 'OnboardingModule/addFeatureToImplementationChecklist':
        this.handleBeforeAddFeatureToImplementationChecklist();
        break;

      case 'OnboardingModule/updateImplementationTaskStatus':
        this.handleBeforeTaskChecks(state);
        break;

      case 'OnboardingModule/updateImplementationTaskStatusAuto':
        this.handleBeforeTaskAutoChecks(action, state);
        break;

      case 'RadiusServerModel/saveItem':
        this.handleBeforeRadiusServerSaved(action, state);
        break;

      case 'SoftwareAppsModel/saveItem':
        this.handleBeforeSoftwareAppSaved(action, state);
        break;

      case 'SystemGroupModel/saveItem':
        this.handleBeforeSystemGroupSaved(action, state);
        break;

      case 'SystemUserModel/saveItem':
        this.handleBeforeSystemUserSaved(action, state);
        break;

      default:
        break;
    }
  },

  after(action, state) {
    switch (action.type) {
      case 'ApplicationCollection/refreshCollection':
        this.handleAfterSamlAppSaved();
        break;

      case 'ApplicationModel/bindApplicationToUserGroups':
        this.handleAfterSamlAppBoundToUserGroups();
        break;

      case 'AuthNPoliciesCollection/refreshCollection':
        this.handleAfterConditionalAccessPolicySaved();
        break;

      case 'CommandModel/saveItem':
        this.handleAfterCommandSaved(action, state);
        break;

      case 'ConfigurationModel/bindConfigurationToSystems':
        this.handleAfterSystemBoundToPolicy();
        break;

      case 'ConfigurationModel/saveItem':
        this.handleAfterConfigurationSaved(action, state);
        break;

      case 'DirectoryCollection/refreshCollection':
        this.handleAfterConfigureLdap();
        break;

      case 'OnboardingModule/addFeatureToImplementationChecklist':
        this.handleAfterAddFeatureToImplementationChecklist(action);
        break;

      case 'OnboardingModule/updateImplementationTaskStatus':
        this.handleAfterTaskChecks(action);
        break;

      case 'OnboardingModule/updateImplementationTaskStatusAuto':
        this.handleAfterTaskAutoChecks(action);
        break;

      case 'LdapModel/fetchItem':
        this.handleAfterLdapVisited(action, state);
        break;

      case 'LdapModel/saveItem':
        this.handleAfterLdapSaved(action, state);
        break;

      case 'MdmSettingsModel/fetchMdmSettings':
        this.handleAfterMdmConfiguration(action, state);
        break;

      case 'MdmSettingsModel/updateDep':
        this.handleAfterDepConfiguration(action, state);
        break;

      case 'RadiusServerModel/bindRadiusServerToUserGroups':
        this.handleAfterRadiusServerBoundToGroups();
        break;

      case 'RadiusServerModel/saveItem':
        this.handleAfterRadiusServerSaved(action, state);
        break;

      case 'SoftwareAppsModel/fetchItem':
        this.handleAfterSoftwareAppVisited(action, state);
        break;

      case 'SoftwareAppsModel/saveItem':
        this.handleAfterSoftwareAppSaved(action, state);
        break;

      case 'SystemCollection/fetchCollection':
      case 'SystemCollection/refreshCollection':
        this.handleAfterSystemSaved();
        break;

      case 'SystemGroupModel/saveItem':
        this.handleAfterSystemGroupSaved(action, state);
        break;

      case 'SystemModel/saveItem':
        this.handleAfterEnableSshPasswordLogin(action, state);
        break;

      case 'SystemUserModel/bindUserToSystems':
        this.handleAfterSystemBoundToUser();
        break;

      case 'SystemUserModel/bindUserToUserGroups':
      case 'UserGroupModel/bindUserGroupToUsers':
        this.handleAfterUserBoundToGroups();
        break;

      case 'SystemUserModel/saveItem':
        this.handleAfterSystemUserSaved(action, state);
        break;

      case 'UserGroupModel/saveItem':
        this.handleAfterUserGroupsSaved(action, state);
        break;

      case 'VppModule/uploadSToken':
        this.handleAfterVppConfiguration(action, state);
        break;

      default:
        break;
    }
  },

  /**
   * Feature added to Implementation Checklist
   */
  handleBeforeAddFeatureToImplementationChecklist() {
    this.implementationChecklistFeaturesComplete = theStore.getters['OnboardingModule/getImplementationChecklistFeaturesComplete'];
    // Reset as more tasks are being added
    this.implementationChecklistComplete = false;
  },

  /**
   * Implementation Checklist task is completed
   */
  handleBeforeTaskChecks() {
    const totalTasks = theStore.getters['OnboardingModule/getImplementationChecklistTotal'];
    const totalTasksComplete = theStore.getters['OnboardingModule/getImplementationChecklistTotalComplete'];
    this.implementationChecklistFeaturesComplete = theStore.getters['OnboardingModule/getImplementationChecklistFeaturesComplete'];

    // If set to true, keep as true
    this.implementationChecklistComplete = totalTasks === totalTasksComplete
        || this.implementationChecklistComplete;
  },

  /**
   * Automatic Implementation Checklist task is completed
   */
  handleBeforeTaskAutoChecks(action) {
    this.handleBeforeTaskChecks();

    const checklists = theStore.getters['OnboardingModule/getImplementationChecklist'];
    checklists.forEach(checklist => {
      const taskIndex = checklist.tasks.findIndex(task => task.taskType === action.payload.taskType
        && !task.complete);

      // trigger Segment event if task is in checklist and not previously completed
      if (taskIndex > -1) {
        const eventText = tasks[implementationChecklistTasks[action.payload.taskType]].headerText;

        EventHelper.triggerCheckboxClicked({
          [boolean]: true,
          [description]: 'Automatically checks off an individual task on the implementation checklist.',
          [location]: eventLocations.implementationChecklistSidebar,
          [section]: `${checklist.headerText} Card`,
          [text]: eventText,
        });
      }
    });
  },

  /**
   * Admin is attempting to create a configuration; save the name to compare
   */
  handleBeforeConfigurationSaved(action, state) {
    if (state.ConfigurationModel.itemState.create === true) {
      this.nameOfConfigurationToBeCreated = state.ConfigurationModel.item.name;
    } else {
      this.nameOfConfigurationToBeCreated = '';
    }
  },

  handleBeforeCommandSaved(action, state) {
    if (state.CommandModel.itemState.create === true) {
      this.commandNameToBeCreated = state.CommandModel.item.name;
    } else {
      this.commandNameToBeCreated = '';
    }
  },

  /**
   * Admin is attempting to create a system group; save the name to compare
   */
  handleBeforeSystemGroupSaved(action, state) {
    if (state.SystemGroupModel.itemState.create === true) {
      this.nameOfSystemGroupToBeCreated = state.SystemGroupModel.item.name;
    } else {
      this.nameOfSystemGroupToBeCreated = '';
    }
  },

  /**
   * Admin is attempting to create a user; save the name to compare
   */
  handleBeforeSystemUserSaved(action, state) {
    if (state.SystemUserModel.itemState.create === true) {
      this.usernameOfUserToBeCreated = state.SystemUserModel.item.username;
    } else {
      this.usernameOfUserToBeCreated = '';
    }
  },

  /**
   * Admin is attempting to create a RADIUS server; save the name to compare
   */
  handleBeforeRadiusServerSaved(action, state) {
    if (state.RadiusServerModel.itemState.create === true) {
      this.radiusServerNameToBeCreated = state.RadiusServerModel.item.name;
    } else {
      this.radiusServerNameToBeCreated = '';
    }
  },

  /**
   * Admin is attempting to create a managed software; save the packageID to compare
   * @param action
   * @param state
   */
  handleBeforeSoftwareAppSaved(action, state) {
    if (state.SoftwareAppsModel.itemState.create === true) {
      this.softwareAppDisplayNameToBeCreated = state.SoftwareAppsModel.item.displayName;
    } else {
      this.softwareAppDisplayNameToBeCreated = '';
    }
  },

  handleAfterMdmConfiguration(action, state) {
    const { mdmDevices } = state.MdmSettingsModel.item;

    if (mdmDevices.length && mdmDevices[0].apnsCertExpiry) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.configureMdm });
    }
  },

  handleAfterDepConfiguration(action, state) {
    const { mdmDevices } = state.MdmSettingsModel.item;
    if (
      mdmDevices.length
        && mdmDevices[0].depAccessTokenExpiry
        && mdmDevices[0].depServerTokenState === 'valid'
    ) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.configureDeviceEnrollmentForMdm });
    }
  },

  handleAfterVppConfiguration(action, state) {
    const { locations } = state.VppModule;

    if (locations.length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.configureVpp });
    }
  },

  handleAfterCommandSaved(action, state) {
    if (state.CommandModel.item.name === this.commandNameToBeCreated) {
      EventHelper.triggerCommandAdded({
        isNew: true,
        os: state.CommandModel.item.commandType,
        basedOnTemplate: state.CommandModel.item.basedOnTemplate,
      });
    }
    this.commandNameToBeCreated = '';
  },

  /**
   * Check to see if an admin has successfully created a user
   */
  handleAfterConfigurationSaved(action, state) {
    if (state.ConfigurationModel.item.name === this.nameOfConfigurationToBeCreated) {
      // parses settings for the configuration props
      const getConfigurationSettings = (configuration) => {
        const values = get(configuration, 'values', []);
        const configFields = get(configuration, 'template.configFields', []);

        const selectedConfigFieldIDs = values
          .filter(entry => !!entry.value)
          .map(entry => entry.configFieldID);

        const selectedConfigFields = configFields
          .filter(field => selectedConfigFieldIDs.includes(field.id))
          .map(field => field.name);

        return selectedConfigFields;
      };

      const newConfiguration = state.ConfigurationModel.item;
      const configurationAddedPayload = {
        isNewPolicy: true,
        name: newConfiguration.name,
        osType: get(newConfiguration, 'template.osMetaFamily', ''),
        settings: getConfigurationSettings(newConfiguration),
      };

      EventHelper.triggerPolicyAdded(configurationAddedPayload);
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createPolicy });
    }

    if (this.nameOfConfigurationToBeCreated === 'JumpCloud MDM Enrollment') {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createMdmEnrollmentPolcy });
    }

    this.nameOfConfigurationToBeCreated = '';
  },

  /**
   * Check to see if an admin has successfully elected LDAP BindDN account
   */
  handleAfterElectLdapAccount() {
    theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.electLdapBindDnAccount });
    theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.configureToUseLdap });
  },

  handleAfterConfigureLdap() {
    if (theStore.getters['DirectoryCollection/isLdap'] && theStore.getters['DirectoryCollection/getCollection'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.configureToUseLdap });
    }
  },

  /**
   * Check to see if an admin has successfully added Conditional Access Policy
   */
  handleAfterConditionalAccessPolicySaved() {
    if (theStore.getters['AuthNPoliciesCollection/getCollection'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createConditionalAccessPolicy });
    }
  },

  /**
   * Check to see if an admin has successfully added SAML app
   */
  handleAfterSamlAppSaved() {
    if (theStore.getters['ApplicationCollection/getCollection'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createExampleSamlApp });
    }
  },

  /**
   * Check to see if a SAML app is bound to a user group
   */
  handleAfterSamlAppBoundToUserGroups() {
    if (theStore.getters['UserGroupCollection/getItemsToBind'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.bindGroupToSamlApp });
    }
  },

  handleAfterSystemUserSaved(action, state) {
    /**
     * Check to see if an admin has successfully created a user
     */
    if (state.SystemUserModel.item.username === this.usernameOfUserToBeCreated) {
      const newUser = state.SystemUserModel.item;
      const userAddedPayload = {
        isNewUser: true,
        specifyInitialPassword: get(newUser, 'activated', false) && !!get(newUser, 'password', ''),
        requireMfa: get(newUser, 'enable_user_portal_multifactor', false),
        enableAdminSudo: get(newUser, 'sudo', false),
        enableLdapBindDn: get(newUser, 'ldap_binding_user', false),
        enforceUidGid: get(newUser, 'enable_managed_uid', ''),
        hasAttributes: get(newUser, 'attributes', []).length > 0,
      };

      EventHelper.triggerUserAdded(userAddedPayload);
      theStore.dispatch('OnboardingModule/completeOnboardingTask', onboardingTasks.userCreated);
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createUser });
    }

    if (state.SystemUserModel.item.ldap_binding_user) {
      this.handleAfterElectLdapAccount();
    }

    this.usernameOfUserToBeCreated = '';
  },

  /**
   * Check to see if a system is bound to a policy
   */
  handleAfterSystemBoundToPolicy() {
    const systems = theStore.getters['SystemCollection/getCollection'];
    const systemsToBind = theStore.getters['SystemCollection/getItemsToBind'];

    if (systems.length && systemsToBind.length) {
      const systemToBind = systems.find(system => system.id === systemsToBind[0].id);
      if (systemToBind.osFamily === 'darwin') {
        theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.bindMdmEnrollmentPolicy });
      }
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.bindPolicyToDevice });
    }
  },

  /**
   * Check to see if a system is bound to a user
   */
  handleAfterSystemBoundToUser() {
    if (theStore.getters['SystemCollection/getItemsToBind'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.bindUserToDevice });
    }
  },

  /**
   * Check to see if an admin has successfully created a system
   */
  handleAfterSystemSaved() {
    if (theStore.getters['SystemCollection/getCollection'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createDevice });
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createServer });
    }
  },

  /**
   * Check to see if an admin has Enabled SSH Password Login
   */
  handleAfterEnableSshPasswordLogin(action, state) {
    if (state.SystemModel.item.allowSshPasswordAuthentication) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.enableSSHPasswordLogin });
    }
  },

  handleAfterAddFeatureToImplementationChecklist() {
    const totalTasks = theStore.getters['OnboardingModule/getImplementationChecklistTotal'];
    const totalTasksComplete = theStore.getters['OnboardingModule/getImplementationChecklistTotalComplete'];

    if (
      totalTasks
        && totalTasksComplete
        && totalTasks === totalTasksComplete
        && !this.implementationChecklistComplete
    ) {
      // If we just added a feature and the checklist is complete
      // that means all the tasks were completed before adding the feature
      // Trigger events for the feautre and again for the checklist being completed
      const featuresComplete = theStore.getters['OnboardingModule/getImplementationChecklistFeaturesComplete'];

      const newFeaturesComplete = difference(
        featuresComplete,
        this.implementationChecklistFeaturesComplete,
      );

      const eventText = 'New feature added to implementation checklist.';

      this.implementationChecklistComplete = true;
      newFeaturesComplete.forEach(async (feature) => {
        const { headerText } = implementationChecklists[feature];
        EventHelper.triggerImplementationChecklistFeatureComplete(
          headerText,
          eventText,
        );
        EventHelper.triggerImplementationChecklistComplete(headerText, eventText);
      });
    }
  },

  /**
   * Check for overall checklist completion
   */
  handleAfterTaskChecks(action) {
    const eventText = tasks[implementationChecklistTasks[action.payload.taskType]].headerText;
    const { complete } = action.payload;

    if (complete) {
      this.handleCheckForChecklistCompletion(eventText);
    }
  },

  /**
   * Check for overall checklist completion
   */
  handleAfterTaskAutoChecks(action) {
    const eventText = tasks[implementationChecklistTasks[action.payload.taskType]].headerText;
    this.handleCheckForChecklistCompletion(eventText);
  },

  /**
   * Based on checklist completion, recommend next feature and trigger events
   */
  handleCheckForChecklistCompletion(eventText) {
    const totalTasks = theStore.getters['OnboardingModule/getImplementationChecklistTotal'];
    const totalTasksComplete = theStore.getters['OnboardingModule/getImplementationChecklistTotalComplete'];

    const featuresComplete = theStore.getters['OnboardingModule/getImplementationChecklistFeaturesComplete'];

    const newFeaturesComplete = difference(
      featuresComplete,
      this.implementationChecklistFeaturesComplete,
    );

    if (newFeaturesComplete.length) {
      // iterate and trigger segment events
      newFeaturesComplete.forEach(async (feature) => {
        const { headerText } = implementationChecklists[feature];
        EventHelper.triggerImplementationChecklistFeatureComplete(
          headerText,
          eventText,
        );

        // Trigger notification/prompt if remote assist feature completed
        // AND admin signed up primarily for Remote Assist
        // AND doesn't already have Device Mgmt in their checklist
        const directOnboardingFeature = theStore.getters['OnboardingModule/getDirectOnboardingFeature'];
        const implementationChecklist = theStore.getters['OnboardingModule/getImplementationChecklist'];
        const deviceFeatureInChecklist = implementationChecklist.some(
          (checklist) => checklist.feature === onboardingFeatures.deviceManagement,
        );
        if (feature === onboardingFeatures.remoteAssist
          && directOnboardingFeature === onboardingFeatures.remoteAssist
          && !deviceFeatureInChecklist) {
          theStore.dispatch('OnboardingModule/addFeatureToRecommendedChecklist', implementationChecklists[onboardingFeatures.deviceManagement]);
          theStore.dispatch(`${notificationModule}/triggerSuccessNotification`, {
            icon: 'checkmarkCircle',
            title: 'Remote Assist tested successfully!',
          });
          theStore.dispatch(`${notificationModule}/triggerNotification`, {
            id: keepGoingNotificationId,
            component: KeepGoingNotification,
            icon: 'info',
            showCloseButton: true,
            title: 'Keep Going!',
          });
        }
      });
    }

    if (
      totalTasks
        && totalTasksComplete
        && totalTasks === totalTasksComplete
        && !this.implementationChecklistComplete
    ) {
      this.implementationChecklistComplete = true;
      newFeaturesComplete.forEach(async (feature) => {
        const { headerText } = implementationChecklists[feature];
        EventHelper.triggerImplementationChecklistComplete(headerText, eventText);
      });
    }
  },

  /**
   * User is bound to user group
   */
  handleAfterUserBoundToGroups() {
    if (theStore.getters['SystemUserCollection/getItemsToBind'].length || theStore.getters['UserGroupCollection/getItemsToBind'].length) {
      EventManager.triggerEvent(events.userBoundToUserGroup);
      theStore.dispatch('OnboardingModule/completeOnboardingTask', onboardingTasks.userBoundToUserGroup);
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.bindUserToUserGroup });
    }
  },

  /**
   * Check to see if a RADIUS server has been successfully added
   */
  handleAfterRadiusServerSaved(action, state) {
    if (state.RadiusServerModel.item.name === this.radiusServerNameToBeCreated) {
      EventHelper.triggerRadiusAdded();
      theStore.dispatch('OnboardingModule/completeOnboardingTask', onboardingTasks.discoverResources);
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createRadius });
    }

    this.radiusServerNameToBeCreated = '';
  },

  /**
   * Check to see if a RADIUS server is bound to user group
   */
  handleAfterRadiusServerBoundToGroups() {
    if (theStore.getters['UserGroupCollection/getItemsToBind'].length) {
      theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.bindGroupToRadius });
    }
  },

  /**
   * An LDAP server aside panel has been visited.
   */
  handleAfterLdapVisited() {
    EventManager.triggerLdapAsidePanelVisited();
  },

  /**
   * An LDAP server is saved.
   */
  handleAfterLdapSaved() {
    const props = { resourceType: 'directory', resourceName: 'ldap' };
    EventHelper.triggerDirectoryAdded(props);
    EventHelper.triggerLdapAdded(props);
    theStore.dispatch('OnboardingModule/completeOnboardingTask', onboardingTasks.discoverResources);
  },

  /**
   * A Software app aside panel has been visited.
   */
  handleAfterSoftwareAppVisited() {
    EventManager.triggerSoftwareAppsAsidePanelVisited();
  },

  /**
   * A Software app is saved.
   */
  handleAfterSoftwareAppSaved(action, state) {
    if (state.SoftwareAppsModel.item.displayName === this.softwareAppDisplayNameToBeCreated) {
      const props = { packageId: state.SoftwareAppsModel.item.settings[0].packageId };
      EventHelper.triggerSoftwareAppAdded(props);
    }

    this.softwareAppDisplayNameToBeCreated = '';
  },

  /**
   * A System Group is saved.
   */
  handleAfterSystemGroupSaved(action, state) {
    if (state.SystemGroupModel.item.name === this.nameOfSystemGroupToBeCreated) {
      const newSystemGroup = state.SystemGroupModel.item;
      const props = {
        groupId: newSystemGroup.id,
        isNewGroup: true,
        resourceType: 'system-group',
      };

      EventHelper.triggerSystemGroupAdded(props);
    }
    this.nameOfSystemGroupToBeCreated = '';
  },

  /**
   * A User Group is saved.
   */
  handleAfterUserGroupsSaved(action, state) {
    const { item } = state.UserGroupModel;
    const props = {
      createLinuxGroup: item.attributes ? item.attributes.ldapGroups : {},
      enableSamba: item.sambaEnabled || false,
      groupId: item.id,
      isNewGroup: true,
      resourceType: 'user-group',
    };

    EventHelper.triggerUserGroupAdded(props);
    theStore.dispatch('OnboardingModule/updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.createUserGroup });
  },

});

// Hook events that are able to be hooked by listening to the store
export const hookEvents = (theStore = store) => {
  theStore.subscribeAction(createSubscribeActionHandler(theStore));
};
